import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Layers, DollarSign } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import UnitsGrid from './units/UnitsGrid';
import UnitsListView from './units/UnitsListView';
import UnitTypesPanel from './units/UnitTypesPanel';
import SiteMap from './SiteMap';
import Promotions from './Promotions';

/**
 * Unified Units page. Operator clicks "Units" in the left nav and lands here.
 * Tabs are query-param routed so deep-links work:
 *
 *   /units                  -> Unit Types (default)
 *   /units?view=list        -> List (dense table: edit / archive / delete)
 *   /units?view=grid        -> Grid status overview
 *   /units?view=map         -> Site Map (drag-and-drop spatial layout)
 *
 * /site-map redirects here via the App.js route table. The old "Manage" tab
 * was removed — the List view is a superset (it now carries the soft-archive
 * + show-archived workflow that used to live in Manage).
 */

// Tab order matches the operator's natural setup flow:
//   1. Define unit types (rate sheet)
//   2. Work the units in a dense list (edit / archive / delete)
//   3. See the grid status overview
//   4. Place units on the visual site map
const TABS = [
  { key: 'types', label: 'Unit Types', desc: 'Rate sheet + descriptions' },
  { key: 'list', label: 'List', desc: 'Dense table with tenant + rate joined in' },
  { key: 'grid', label: 'Grid', desc: 'Type-grouped status overview' },
  { key: 'map', label: 'Site Map', desc: 'Physical layout' },
  { key: 'promotions', label: 'Promotions', desc: 'Move-in discounts: percent, flat, or free-period' },
];

const UnitsPage = () => {
  const { facilityId, loading: facilityLoading } = useFacility();
  const [searchParams, setSearchParams] = useSearchParams();
  // Default tab is 'types' (matches the first tab in the strip above).
  const activeTab = searchParams.get('view') || 'types';

  const setTab = (key) => {
    const params = new URLSearchParams(searchParams);
    if (key === 'types') params.delete('view');
    else params.set('view', key);
    setSearchParams(params, { replace: true });
  };

  if (facilityLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!facilityId) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <h2 className="text-lg font-semibold text-slate-900">
          No facility selected
        </h2>
        <p className="text-[13px] text-slate-500 mt-1">
          Create a facility in Settings to manage units.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Units</h1>
          <p className="text-[13px] text-slate-400 mt-0.5">
            {TABS.find((t) => t.key === activeTab)?.desc}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          <Link
            to="/units/mass-edit-rates"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3.5 sm:py-2 sm:text-[13px] rounded-lg bg-blue-600 font-medium text-white hover:bg-blue-700 transition-colors"
          >
            <DollarSign className="h-3.5 w-3.5" />
            Bulk rate change
          </Link>
          <Link
            to="/units/bulk"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3.5 sm:py-2 sm:text-[13px] rounded-lg bg-blue-600 font-medium text-white hover:bg-blue-700 transition-colors"
          >
            <Layers className="h-3.5 w-3.5" />
            Bulk Add
          </Link>
          <Link
            to="/units/new"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] sm:px-3.5 sm:py-2 sm:text-[13px] rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            New Unit
          </Link>
        </div>
      </div>

      {/* Tab strip */}
      <div className="border-b border-slate-200 mb-5">
        <nav className="-mb-px flex gap-6" aria-label="Units tabs">
          {TABS.map((t) => {
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`whitespace-nowrap py-2.5 px-1 border-b-2 text-[13px] font-medium transition-colors ${
                  active
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Active tab content. Each tab component is lazily-instantiated by
          virtue of conditional rendering — heavy SiteMap state machine only
          loads when its tab is active. */}
      {activeTab === 'types' && <UnitTypesPanel facilityId={facilityId} />}
      {activeTab === 'list' && <UnitsListView facilityId={facilityId} />}
      {activeTab === 'grid' && <UnitsGrid facilityId={facilityId} />}
      {activeTab === 'map' && <SiteMap />}
      {/* Promotions reads facilityId from useFacility() context, so no prop
          is needed — it renders chrome-free embedded inside this tab. */}
      {activeTab === 'promotions' && <Promotions />}
    </div>
  );
};

export default UnitsPage;
